'use client';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { WORKSPACE_STORAGE_KEY } from '@/constants/workspace';

const SERVICE_NAME = process.env.NEXT_PUBLIC_DATADOG_SERVICE || 'mb-frontend';
const ENVIRONMENT = (process.env.NEXT_PUBLIC_DATADOG_ENV || 'development').toLowerCase();

// Check if we're in development mode (not production)
// Stringify objects in console logs when NOT in production
// Prioritize NEXT_PUBLIC_APP_ENV over NODE_ENV since NODE_ENV is set at build time
// and may be 'production' even on dev servers
const appEnv = process.env.NEXT_PUBLIC_APP_ENV?.toLowerCase();
const nodeEnv = process.env.NODE_ENV?.toLowerCase();
// If NEXT_PUBLIC_APP_ENV is explicitly set, use it; otherwise fall back to NODE_ENV
const effectiveEnv = appEnv || nodeEnv;
const IS_DEV = !(effectiveEnv === 'production' || effectiveEnv === 'prod');

// Debug logging (only in browser, and only log once)
if (typeof window !== 'undefined' && !(window as any).__LOGGER_DEBUG_LOGGED) {
  console.log('[logger] Environment check:', { appEnv, nodeEnv, effectiveEnv, IS_DEV });
  (window as any).__LOGGER_DEBUG_LOGGED = true;
}

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext extends Record<string, unknown> {
  workspaceId?: string | number;
  userId?: string | number;
  audienceId?: string | number;
}

const fallbackConsole: Record<LogLevel, (...args: unknown[]) => void> = {
  debug: console.debug.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

const isBrowser = () => typeof window !== 'undefined';

const safeLocalStorageGet = (key: string): string | undefined => {
  if (!isBrowser()) return undefined;
  try {
    const value = window.localStorage.getItem(key);
    if (!value || value === 'null' || value === 'undefined') {
      return undefined;
    }
    return value;
  } catch {
    return undefined;
  }
};

const sanitizeValue = (value: unknown): unknown => {
  if (value instanceof Error) {
    return {
      message: value.message,
      name: value.name,
      stack: value.stack,
    };
  }

  return value;
};


const buildContext = (context: LogContext = {}) => {
  const normalized: Record<string, unknown> = {};

  Object.entries(context).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }

    if (key === 'workspaceId' && value !== undefined && value !== null) {
      normalized.workspace_id = value;
      return;
    }

    if (key === 'userId' && value !== undefined && value !== null) {
      normalized.user_id = value;
      return;
    }

    if (key === 'audienceId' && value !== undefined && value !== null) {
      normalized.audience_id = value;
      return;
    }

    normalized[key] = sanitizeValue(value);
  });

  if (!normalized.workspace_id) {
    const storedWorkspace = safeLocalStorageGet(WORKSPACE_STORAGE_KEY);
    if (storedWorkspace) {
      normalized.workspace_id = storedWorkspace;
    }
  }

  normalized.service = normalized.service || SERVICE_NAME;
  normalized.env = normalized.env || ENVIRONMENT;

  return normalized;
};

const sendLog = (level: LogLevel, message: string, context?: LogContext) => {
  const payload = buildContext(context);

  if (isBrowser()) {
    try {
      datadogLogs.logger.log(message, payload, level);
      return;
    } catch (error) {
      if (IS_DEV) {
        try {
          const errorPayload = { level, message, context: payload };
          fallbackConsole.warn('[logger] Failed to send log to Datadog Logs', error, JSON.stringify(errorPayload, null, 2));
        } catch {
          fallbackConsole.warn('[logger] Failed to send log to Datadog Logs', error, { level, message, context: payload });
        }
      } else {
        fallbackConsole.warn('[logger] Failed to send log to Datadog Logs', error, { level, message, context: payload });
      }
    }
  }

  if (IS_DEV) {
    try {
      fallbackConsole[level](`[${SERVICE_NAME.toUpperCase()}][${level.toUpperCase()}] ${message}`, JSON.stringify(payload, null, 2));
    } catch {
      // If stringification fails, fall back to original payload
      fallbackConsole[level](`[${SERVICE_NAME.toUpperCase()}][${level.toUpperCase()}] ${message}`, payload);
    }
  } else {
    fallbackConsole[level](`[${SERVICE_NAME.toUpperCase()}][${level.toUpperCase()}] ${message}`, payload);
  }
};

const serializeError = (error: unknown) => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }

  if (typeof error === 'string') {
    return { message: error };
  }

  if (error && typeof error === 'object') {
    return error;
  }

  return { message: 'Unknown error value' };
};

const trackError = (error: unknown, context?: LogContext & { message?: string }) => {
  const serializedError = serializeError(error) as Record<string, unknown>;
  const message = context?.message || (serializedError.message as string) || 'Frontend error';
  const logContext: LogContext = {
    ...context,
    error: serializedError,
  };

  sendLog('error', message, logContext);

  if (!isBrowser()) {
    return;
  }

  try {
    datadogRum.addError(error instanceof Error ? error : new Error(message), buildContext(logContext));
  } catch (err) {
    if (IS_DEV) {
      try {
        const errorObj = typeof err === 'object' && err !== null ? err : { error: err };
        fallbackConsole.warn('[logger] Failed to forward error to Datadog RUM', JSON.stringify(errorObj, null, 2));
      } catch {
        fallbackConsole.warn('[logger] Failed to forward error to Datadog RUM', err);
      }
    } else {
      fallbackConsole.warn('[logger] Failed to forward error to Datadog RUM', err);
    }
  }
};

const trackAction = (action: string, context?: LogContext) => {
  sendLog('info', `action:${action}`, context);

  if (!isBrowser()) {
    return;
  }

  try {
    datadogRum.addAction(action, buildContext(context));
  } catch (error) {
    if (IS_DEV) {
      try {
        const errorObj = typeof error === 'object' && error !== null ? error : { error };
        const contextObj = { action, context };
        fallbackConsole.warn('[logger] Failed to record action in Datadog RUM', JSON.stringify(errorObj, null, 2), JSON.stringify(contextObj, null, 2));
      } catch {
        fallbackConsole.warn('[logger] Failed to record action in Datadog RUM', error, { action, context });
      }
    } else {
      fallbackConsole.warn('[logger] Failed to record action in Datadog RUM', error, { action, context });
    }
  }
};

const appLogger = {
  debug: (message: string, context?: LogContext) => sendLog('debug', message, context),
  info: (message: string, context?: LogContext) => sendLog('info', message, context),
  warn: (message: string, context?: LogContext) => sendLog('warn', message, context),
  error: (message: string, context?: LogContext) => sendLog('error', message, context),
  trackError,
  trackAction,
};

export default appLogger;
